import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ClientJobsTab.module.css";
import { jobsService, } from "@/api/services/jobs.service";
import { useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { useAuth } from "@/context/AuthContext";
import { getRoutePrefix } from "@/utils/navigation";
import { formatJobAddress } from "@/utils/address";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import { Role } from "@/types/auth.types";
const JOB_SECTIONS = [
    { id: "new_project", label: "NEW PROJECT" },
    { id: "in_progress", label: "IN PROGRESS" },
    { id: "scheduled", label: "SCHEDULED" },
    { id: "on_hold", label: "ON HOLD" },
    { id: "completed", label: "COMPLETED" },
    { id: "invoiced", label: "INVOICED" },
];
// Icons
const LocationIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
const CalendarIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ClockIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
const ToolIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }) }));
const ChevronIcon = ({ isOpen }) => (_jsx("svg", { width: "32", height: "32", viewBox: "0 0 24 24", fill: isOpen ? "#f25b22" : "var(--color-sapphire)", style: {
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease, fill 0.2s ease",
    }, children: _jsx("path", { d: "M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" }) }));
export const ClientJobsTab = ({ companyName }) => {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [counts, setCounts] = useState({
        not_started: 0,
        new_project: 0,
        in_progress: 0,
        scheduled: 0,
        on_hold: 0,
        completed: 0,
        invoiced: 0,
        cancelled: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedSections, setExpandedSections] = useState(new Set(["in_progress", "scheduled"]));
    const [cancellingJob, setCancellingJob] = useState(null);
    const fetchJobs = useCallback(async () => {
        if (!contactId && !companyName)
            return;
        try {
            setLoading(true);
            setError(null);
            // When a company name is available, load jobs using the generic
            // search-based endpoint so we aggregate all jobs for the company
            // (across all contacts) and then compute counts client-side.
            if (companyName) {
                const response = await jobsService.getJobs({
                    search: companyName,
                    pageSize: 1000,
                });
                const allJobs = response.data.data;
                setJobs(allJobs);
                const nextCounts = {
                    not_started: 0,
                    new_project: 0,
                    in_progress: 0,
                    scheduled: 0,
                    on_hold: 0,
                    completed: 0,
                    invoiced: 0,
                    cancelled: 0,
                };
                for (const job of allJobs) {
                    const key = job.status;
                    nextCounts[key] = (nextCounts[key] ?? 0) + 1;
                }
                setCounts(nextCounts);
            }
            else if (contactId) {
                const [jobsResponse, countsResponse] = await Promise.all([
                    jobsService.getJobsByContact(contactId),
                    jobsService.getJobCountsRawByContact(contactId),
                ]);
                setJobs(jobsResponse.data);
                setCounts(countsResponse.data);
            }
        }
        catch (err) {
            setError("Failed to load jobs");
            console.error("Error fetching jobs:", err);
        }
        finally {
            setLoading(false);
        }
    }, [companyName, contactId, contactId]);
    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);
    const handleExpandAll = () => {
        setExpandedSections(new Set(JOB_SECTIONS.map((s) => s.id)));
    };
    const handleCollapseAll = () => {
        setExpandedSections(new Set());
    };
    const toggleSection = (sectionId) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        }
        else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };
    const getJobsByStatus = (status) => {
        return jobs.filter((job) => job.status === status &&
            (searchTerm === "" ||
                job.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.title.toLowerCase().includes(searchTerm.toLowerCase())));
    };
    const formatDate = (dateString) => dateString ? formatDateOnlyDisplay(dateString) : "TBD";
    const formatTimeRange = (startTime, endTime) => {
        if (!startTime && !endTime)
            return "TBD";
        return `${startTime || "TBD"} - ${endTime || "TBD"}`;
    };
    const formatDuration = (days) => {
        if (!days)
            return "TBD";
        return days === 1 ? "1 Day" : `${days} Days`;
    };
    const handleViewJob = (jobId) => {
        // Navigate to job edit page (where job details are viewed/edited)
        const routePrefix = getRoutePrefix(user?.role || Role.ADMIN);
        navigate(`${routePrefix}/jobs/${jobId}/edit`);
    };
    const handleCancelJob = async (jobId) => {
        const confirmed = await confirm("Are you sure you want to cancel this job?");
        if (!confirmed)
            return;
        try {
            setCancellingJob(jobId);
            await jobsService.cancelJob(jobId);
            // Update local state
            setJobs((prev) => prev.map((job) => job.id === jobId ? { ...job, status: "cancelled" } : job));
            // Update counts
            setCounts((prev) => ({
                ...prev,
                cancelled: prev.cancelled + 1,
            }));
        }
        catch (err) {
            console.error("Error cancelling job:", err);
            showError("Failed to cancel job");
        }
        finally {
            setCancellingJob(null);
        }
    };
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading jobs..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchJobs, className: styles.retryButton, children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.header, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsxs("div", { className: styles.headerActions, children: [_jsxs("button", { className: styles.expandBtn, onClick: handleExpandAll, children: ["EXPAND ALL", _jsx(ChevronIcon, { isOpen: true })] }), _jsxs("button", { className: styles.collapseBtn, onClick: handleCollapseAll, children: ["COLLAPSE ALL", _jsx(ChevronIcon, { isOpen: false })] })] })] }), _jsx("div", { className: styles.sections, children: JOB_SECTIONS.map((section) => {
                    const sectionJobs = getJobsByStatus(section.id);
                    const isExpanded = expandedSections.has(section.id);
                    const count = counts[section.id] || 0;
                    return (_jsxs("div", { className: styles.section, children: [_jsxs("button", { className: styles.sectionHeader, onClick: () => toggleSection(section.id), children: [_jsxs("div", { className: styles.sectionTitle, children: [_jsx("span", { className: styles.sectionLabel, children: section.label }), count > 0 && (_jsx("span", { className: styles.sectionCount, children: count }))] }), _jsx(ChevronIcon, { isOpen: isExpanded })] }), isExpanded && (_jsx("div", { className: styles.sectionContent, children: sectionJobs.length === 0 ? (_jsx("div", { className: styles.emptyState, children: _jsx("p", { children: "No jobs found" }) })) : (_jsx("div", { className: styles.jobsGrid, children: sectionJobs.map((job) => (_jsxs("div", { className: styles.jobCard, children: [_jsx("div", { className: styles.jobImage, children: job.imageUrl ? (_jsx("img", { src: job.imageUrl, alt: `Job ${job.jobNumber}`, onError: (e) => {
                                                        e.target.src =
                                                            "/images/Defit Social Media 3.png";
                                                    } })) : (_jsx("div", { className: styles.jobImagePlaceholder, children: _jsx(ToolIcon, {}) })) }), _jsxs("div", { className: styles.jobDetails, children: [_jsxs("div", { className: styles.jobHeader, children: [_jsxs("div", { className: styles.jobTitle, children: ["Job #", job.jobNumber] }), _jsx("div", { className: styles.jobName, children: job.title })] }), _jsxs("div", { className: styles.jobInfoGrid, children: [_jsxs("div", { className: styles.jobInfoItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: formatJobAddress(job) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(CalendarIcon, {}), _jsx("span", { children: formatDate(job.scheduledDate) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatTimeRange(job.startTime, job.endTime) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatDuration(job.durationDays) })] })] }), _jsxs("div", { className: styles.jobActions, children: [_jsxs("button", { className: styles.viewJobBtn, onClick: () => handleViewJob(job.id), children: [_jsx(ToolIcon, {}), "VIEW JOB"] }), job.status !== "completed" &&
                                                                job.status !== "cancelled" && (_jsx("button", { className: styles.cancelBtn, onClick: () => handleCancelJob(job.id), disabled: cancellingJob === job.id, children: cancellingJob === job.id
                                                                    ? "..."
                                                                    : "CANCEL" }))] })] })] }, job.id))) })) }))] }, section.id));
                }) })] }));
};
